import { useMemo } from "react";
import type { TransporterRunHistoryDto } from "../../lib/fetch-transporter-delivery-ops";

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export function RecentDeliveryLogsTable({
  rows,
  filterQuery = "",
}: {
  rows: TransporterRunHistoryDto[];
  /** Workspace header search (`/workspace/dashboard?q=`). */
  filterQuery?: string;
}) {
  const visibleRows = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.driverFullName,
        r.id,
        r.dispensaryLegalName,
        r.status,
        r.metrcManifestNumber ?? "",
        r.manifestStatus ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filterQuery]);

  return (
    <section>
      <h3 className="mb-4 text-base font-bold text-td-ink">Recent Delivery Logs</h3>
      <div className="td-surface-soft overflow-hidden">
        <table className="td-data-table">
          <thead>
            <tr>
              <th>Transporter</th>
              <th>Order ID</th>
              <th>Destination</th>
              <th>Delivery Time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="!py-8 text-center text-sm text-td-muted">
                  No delivery logs yet.
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={5} className="!py-8 text-center text-sm text-td-muted">
                  No delivery logs match your search.
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F0F0F0] text-xs font-bold text-[#555555]">
                        {r.driverFullName.slice(0, 1).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-[#333333]">{r.driverFullName}</span>
                    </div>
                  </td>
                  <td>
                    <span className="text-sm font-bold text-td-ink">#{r.id.slice(0, 8)}</span>
                  </td>
                  <td className="text-sm text-[#555555]">{r.dispensaryLegalName}</td>
                  <td className="text-sm text-[#555555]">{fmtTime(r.completedAt ?? r.updatedAt)}</td>
                  <td>
                    <StatusBadge status={r.status} completed={r.completedAt != null} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatusBadge({ status, completed }: { status: string; completed: boolean }) {
  const s = status.toLowerCase();
  if (completed || s.includes("complete") || s.includes("deliver")) {
    return (
      <span className="rounded-lg bg-td-status-delivered-bg px-3 py-1 text-xs font-medium text-td-status-delivered-text">
        Delivered
      </span>
    );
  }
  if (s.includes("transit") || s.includes("active") || s.includes("progress")) {
    return <span className="rounded-lg bg-[#111111] px-3 py-1 text-xs font-medium text-white">In Transit</span>;
  }
  return (
    <span className="rounded-lg bg-[#F0F0F0] px-3 py-1 text-xs font-medium text-[#555555]">{status}</span>
  );
}
